import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { FaLock, FaUserInjured, FaUserMd } from 'react-icons/fa';
import { AppProvider, useApp } from './context/AppContext';

import Login         from './pages/Login';
import VerifyEmail   from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import Dashboard      from './pages/Dashboard';
import Appointments   from './pages/Appointments';
import Patients       from './pages/Patients';
import Prescriptions  from './pages/Prescriptions';
import LabTests       from './pages/LabTests';
import Billing        from './pages/Billing';
import Telemedicine   from './pages/Telemedicine';
import EMR            from './pages/EMR';
import Pharmacy       from './pages/Pharmacy';
import Calendar       from './pages/Calendar';
import Reports        from './pages/Reports';
import Messages       from './pages/Messages';
import Settings       from './pages/Settings';
import Profile        from './pages/Profile';
import NewAppointment from './pages/NewAppointment';
import AdminPanel     from './pages/AdminPanel';
import Notifications  from './pages/Notifications';
import Specialists    from './pages/Specialists';
import HelpSupport    from './pages/HelpSupport';

const Toast = () => {
  const { toast } = useApp();
  if (!toast) return null;
  return <div className={`toast ${toast.type}`}>{toast.message}</div>;
};

// "Please register / log in first" — shown when unauthenticated user hits a role route
const RegisterFirstScreen = ({ role }) => {
  const navigate = useNavigate();
  const r = (role || 'patient').toLowerCase();
  const label = r.charAt(0).toUpperCase() + r.slice(1);
  const isAdmin = r === 'admin';
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#f0f2f5', padding: 20,
    }}>
      <div style={{
        background: 'white', borderRadius: 20, padding: '48px 40px',
        maxWidth: 420, width: '100%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', margin: '0 auto 24px',
          background: 'linear-gradient(135deg,#E8500A,#FF6B2C)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(232,80,10,0.35)',
        }}>
          <FaLock size={32} color="white" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e', marginBottom: 12 }}>
          Please Register First
        </h2>
        <p style={{ fontSize: 14, color: '#8888a8', lineHeight: 1.7, marginBottom: 32 }}>
          You need a <strong>{label}</strong> account to access this section.
          {!isAdmin && ' Please log in or create an account to continue.'}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => navigate(`/${r}/login`)}
            style={{
              flex: 1, padding: '14px 0', borderRadius: 10,
              border: '2px solid #E8500A', background: 'white',
              color: '#E8500A', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >
            {isAdmin ? <><FaUserMd style={{ marginRight: 6 }} />Login</> : <><FaUserInjured style={{ marginRight: 6 }} />Login</>}
          </button>
          {!isAdmin && (
            <button
              onClick={() => navigate(`/${r}/signup`)}
              style={{
                flex: 1, padding: '14px 0', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', color: 'white',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(232,80,10,0.35)',
              }}
            >
              Register
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Route guard: if not logged in → RegisterFirstScreen, if wrong role → redirect to correct role base
const RoleGuard = ({ role }) => {
  const { user } = useApp();
  if (!user) return <RegisterFirstScreen role={role} />;
  if (user.role.toLowerCase() !== role.toLowerCase()) {
    return <Navigate to={`/${user.role.toLowerCase()}`} replace />;
  }
  return <Outlet />;
};

// Redirect / to role-specific home
const RoleRedirect = () => {
  const { user } = useApp();
  if (!user) return <Navigate to="/patient/login" replace />;
  return <Navigate to={`/${user.role.toLowerCase()}`} replace />;
};

// Redirect already-logged-in users away from auth pages
const AuthRoute = ({ role, children }) => {
  const { user } = useApp();
  if (user) {
    return <Navigate to={`/${user.role.toLowerCase()}`} replace />;
  }
  return children;
};

function AppContent() {
  return (
    <>
      <Router>
        <Routes>
          {/* ── Public / Auth ─────────────────────────────────────── */}
          <Route path="/patient/login"  element={<AuthRoute role="Patient"><Login role="Patient" /></AuthRoute>} />
          <Route path="/patient/signup" element={<AuthRoute role="Patient"><Login role="Patient" defaultMode="register" /></AuthRoute>} />
          <Route path="/doctor/login"   element={<AuthRoute role="Doctor"><Login role="Doctor" /></AuthRoute>} />
          <Route path="/doctor/signup"  element={<AuthRoute role="Doctor"><Login role="Doctor" defaultMode="register" /></AuthRoute>} />
          <Route path="/admin/login"    element={<AuthRoute role="Admin"><Login role="Admin" /></AuthRoute>} />

          {/* Legacy ── redirect old /login to patient login */}
          <Route path="/login"  element={<Navigate to="/patient/login"  replace />} />
          <Route path="/signup" element={<Navigate to="/patient/signup" replace />} />

          <Route path="/verify-email"    element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />

          {/* ── Patient routes ───────────────────────────────────────── */}
          <Route path="/patient" element={<RoleGuard role="Patient" />}>
            <Route index                   element={<Dashboard />} />
            <Route path="appointments"     element={<Appointments />} />
            <Route path="new-appointment"  element={<NewAppointment />} />
            <Route path="specialists"      element={<Specialists />} />
            <Route path="prescriptions"    element={<Prescriptions />} />
            <Route path="labtests"         element={<LabTests />} />
            <Route path="billing"          element={<Billing />} />
            <Route path="telemedicine"     element={<Telemedicine />} />
            <Route path="emr"              element={<EMR />} />
            <Route path="pharmacy"         element={<Pharmacy />} />
            <Route path="messages"         element={<Messages />} />
            <Route path="notifications"    element={<Notifications />} />
            <Route path="settings"         element={<Settings />} />
            <Route path="profile"          element={<Profile />} />
            <Route path="help"             element={<HelpSupport />} />
          </Route>

          {/* ── Doctor routes ────────────────────────────────────────── */}
          <Route path="/doctor" element={<RoleGuard role="Doctor" />}>
            <Route index                   element={<Dashboard />} />
            <Route path="appointments"     element={<Appointments />} />
            <Route path="new-appointment"  element={<NewAppointment />} />
            <Route path="specialists"      element={<Specialists />} />
            <Route path="patients"         element={<Patients />} />
            <Route path="prescriptions"    element={<Prescriptions />} />
            <Route path="labtests"         element={<LabTests />} />
            <Route path="billing"          element={<Billing />} />
            <Route path="telemedicine"     element={<Telemedicine />} />
            <Route path="emr"              element={<EMR />} />
            <Route path="pharmacy"         element={<Pharmacy />} />
            <Route path="calendar"         element={<Calendar />} />
            <Route path="reports"          element={<Reports />} />
            <Route path="messages"         element={<Messages />} />
            <Route path="notifications"    element={<Notifications />} />
            <Route path="settings"         element={<Settings />} />
            <Route path="profile"          element={<Profile />} />
            <Route path="help"             element={<HelpSupport />} />
          </Route>

          {/* ── Admin routes ─────────────────────────────────────────── */}
          <Route path="/admin" element={<RoleGuard role="Admin" />}>
            <Route index                   element={<Dashboard />} />
            <Route path="appointments"     element={<Appointments />} />
            <Route path="new-appointment"  element={<NewAppointment />} />
            <Route path="specialists"      element={<Specialists />} />
            <Route path="patients"         element={<Patients />} />
            <Route path="prescriptions"    element={<Prescriptions />} />
            <Route path="labtests"         element={<LabTests />} />
            <Route path="billing"          element={<Billing />} />
            <Route path="telemedicine"     element={<Telemedicine />} />
            <Route path="emr"              element={<EMR />} />
            <Route path="pharmacy"         element={<Pharmacy />} />
            <Route path="calendar"         element={<Calendar />} />
            <Route path="reports"          element={<Reports />} />
            <Route path="messages"         element={<Messages />} />
            <Route path="notifications"    element={<Notifications />} />
            <Route path="settings"         element={<Settings />} />
            <Route path="profile"          element={<Profile />} />
            <Route path="help"             element={<HelpSupport />} />
            <Route path="panel"            element={<AdminPanel />} />
          </Route>

          {/* ── Root & catch-all ─────────────────────────────────────── */}
          <Route path="/"  element={<RoleRedirect />} />
          <Route path="*"  element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <Toast />
    </>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
